/* Graficos SVG do assistente (21/10/2026): rosca e linha/area, sem dependencia. */

const fmt = (v: number) => v.toLocaleString('pt-BR');
// Paleta categorica de tons medios: legivel sobre fundo claro e escuro.
const PALETA = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16'];

type Item = { rotulo: string; valor: number };

export function Pizza({ titulo, itens }: { titulo?: string; itens: Item[] }) {
  const validos = (itens || []).filter((x) => x.valor > 0).sort((a, b) => b.valor - a.valor);
  if (!validos.length) return <Vazio titulo={titulo} />;
  // Mais de 8: as 7 maiores + "Outros".
  const fatias =
    validos.length > 8
      ? [...validos.slice(0, 7), { rotulo: 'Outros', valor: validos.slice(7).reduce((s, x) => s + x.valor, 0) }]
      : validos;
  const total = fatias.reduce((s, x) => s + x.valor, 0);
  const R = 42;
  const C = 2 * Math.PI * R;
  let acc = 0;
  const desc = fatias.map((f) => `${f.rotulo} ${fmt(f.valor)}`).join(', ');
  return (
    <div className="space-y-1.5">
      {titulo && <div className="text-[12px] font-semibold text-ink">{titulo}</div>}
      <div className="flex flex-wrap items-center gap-3">
        <svg viewBox="0 0 120 120" className="h-[120px] w-[120px] shrink-0" role="img" aria-label={`Gráfico de rosca${titulo ? ` ${titulo}` : ''}: ${desc}`}>
          <g transform="rotate(-90 60 60)">
            {fatias.map((f, i) => {
              const len = (f.valor / total) * C;
              const el = (
                <circle key={i} cx="60" cy="60" r={R} fill="none" stroke={PALETA[i]} strokeWidth="18" strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-acc}>
                  <title>{`${f.rotulo}: ${fmt(f.valor)} (${Math.round((f.valor / total) * 100)}%)`}</title>
                </circle>
              );
              acc += len;
              return el;
            })}
          </g>
          <text x="60" y="60" textAnchor="middle" dominantBaseline="central" className="fill-current text-ink" fontSize="15" fontWeight="600">
            {fmt(total)}
          </text>
        </svg>
        <ul className="min-w-[140px] flex-1 space-y-1 text-[12px]">
          {fatias.map((f, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: PALETA[i] }} />
              <span className="truncate text-ink">{f.rotulo}</span>
              <span className="ml-auto shrink-0 text-muted-foreground">
                {fmt(f.valor)} · {Math.round((f.valor / total) * 100)}%
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Passo "redondo" (1, 2, 5 x 10^n) para o eixo Y.
function passoBonito(max: number, marcas: number) {
  const bruto = max / marcas;
  const exp = Math.pow(10, Math.floor(Math.log10(bruto)));
  const f = bruto / exp;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10) * exp;
}

export function Linha({ titulo, pontos }: { titulo?: string; pontos: Item[] }) {
  if (!pontos?.length) return <Vazio titulo={titulo} />;
  const W = 320;
  const H = 160;
  const E = 40; // margem esquerda (rotulos do eixo Y)
  const D = 10;
  const T = 10;
  const B = 24;
  const max = Math.max(...pontos.map((p) => p.valor), 0);
  const passo = max > 0 ? passoBonito(max, 3) : 1;
  const n = Math.max(1, Math.ceil(max / passo));
  const topo = passo * n;
  const x = (i: number) => (pontos.length === 1 ? E + (W - E - D) / 2 : E + (i / (pontos.length - 1)) * (W - E - D));
  const y = (v: number) => T + (1 - Math.max(0, v) / topo) * (H - T - B);
  const linha = pontos.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(p.valor).toFixed(1)}`).join(' ');
  const area = `${linha} L${x(pontos.length - 1).toFixed(1)} ${H - B} L${x(0).toFixed(1)} ${H - B} Z`;
  const pulo = Math.ceil(pontos.length / 6); // no maximo ~6 rotulos no eixo X
  const ult = pontos[pontos.length - 1];
  return (
    <div className="space-y-1.5">
      {titulo && <div className="text-[12px] font-semibold text-ink">{titulo}</div>}
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        role="img"
        aria-label={`Gráfico de linha${titulo ? ` ${titulo}` : ''}: ${pontos.length} pontos, de ${pontos[0].rotulo} a ${ult.rotulo}, último valor ${fmt(ult.valor)}, máximo do eixo ${fmt(topo)}`}
      >
        {Array.from({ length: n + 1 }, (_, k) => {
          const v = k * passo;
          return (
            <g key={k}>
              <line x1={E} x2={W - D} y1={y(v)} y2={y(v)} className="stroke-current text-line" strokeWidth="1" />
              <text x={E - 5} y={y(v)} textAnchor="end" dominantBaseline="central" className="fill-current text-muted-foreground" fontSize="9">
                {fmt(v)}
              </text>
            </g>
          );
        })}
        <path d={area} fill="#3b82f6" fillOpacity="0.15" />
        <path d={linha} fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {pontos.map((p, i) => (
          <g key={i}>
            <circle cx={x(i)} cy={y(p.valor)} r="3" fill="#3b82f6" stroke="currentColor" className="text-surface" strokeWidth="1">
              <title>{`${p.rotulo}: ${fmt(p.valor)}`}</title>
            </circle>
            {i % pulo === 0 && (
              <text x={x(i)} y={H - B + 12} textAnchor="middle" className="fill-current text-muted-foreground" fontSize="9">
                {p.rotulo}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
}

function Vazio({ titulo }: { titulo?: string }) {
  return (
    <div className="space-y-1">
      {titulo && <div className="text-[12px] font-semibold text-ink">{titulo}</div>}
      <div className="text-[12px] text-muted-foreground">Sem dados</div>
    </div>
  );
}
